import { inject, Injectable } from '@angular/core';
import { LocationsDataService } from '@app/shared/services/locations-data.service';
import { ProjectsDataService } from '@app/shared/services/projects-data.service';
import { TasksDataService } from '@app/shared/services/tasks-data.service';
import { Location } from '@app/shared/types/models/location/location.model';
import { Project } from '@app/shared/types/models/project/project.model';
import { TimeReport } from '@app/shared/types/models/reports/time-report';
import { isWorkTimeReport, WorkTimeReport } from '@app/shared/types/models/reports/work-time-report';
import { Task } from '@app/shared/types/models/task/task.model';

type WorkTimeReportTask = NonNullable<WorkTimeReport['task']>;
type WorkTimeReportProject = NonNullable<WorkTimeReport['project']>;
type WorkTimeReportLocation = NonNullable<WorkTimeReport['location']>;

/**
 * WorkTimeReportContextMapper - Builds WorkTime copies with task/project/location from loaded stores.
 *
 * Report fields win over store data. Nested source objects are never mutated.
 * Store signals are read whenever an id is present, so later context still hydrates.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: {@link WorkTimeReportComponent} hydrates mixed Reports lists before filters, table, grouping, and charts.
 *
 * Architecture:
 * - {@link WorkTimeReportContextMapper}: Read-only store hydration, no HTTP
 * - {@link TimeReportRowMapper}: Maps hydrated reports to table rows
 * - {@link TimeReportsDataService}: Mixed report cache and writes
 */
@Injectable({ providedIn: 'root' })
export class WorkTimeReportContextMapper {
  private readonly tasksDataService = inject(TasksDataService);
  private readonly projectsDataService = inject(ProjectsDataService);
  private readonly locationsDataService = inject(LocationsDataService);

  public hydrate(report: WorkTimeReport): WorkTimeReport;
  public hydrate(report: TimeReport): TimeReport;
  public hydrate(report: TimeReport): TimeReport {
    if (!isWorkTimeReport(report)) {
      return report;
    }

    return this.hydrateWorkTime(report);
  }

  public hydrateMany(reports: TimeReport[]): TimeReport[] {
    return reports.map(report => this.hydrate(report));
  }

  private hydrateWorkTime(report: WorkTimeReport): WorkTimeReport {
    const storedTask = this.readStoredTask(report.taskId);
    const task = this.resolveTask(report, storedTask);
    const projectId = this.resolveProjectId(report, storedTask);
    const locationId = this.resolveLocationId(report, storedTask);
    const storedProject = this.readStoredProject(projectId);
    const storedLocation = this.readStoredLocation(locationId);

    return new WorkTimeReport({
      ...report,
      task,
      projectId,
      locationId,
      project: this.resolveProject(report, storedProject),
      location: this.resolveLocation(report, storedLocation)
    });
  }

  private readStoredTask(taskId: string | undefined): Task | undefined {
    return taskId ? this.tasksDataService.getById(taskId)() : undefined;
  }

  private readStoredProject(projectId: string | undefined): Project | undefined {
    return projectId ? this.projectsDataService.getById(projectId)() : undefined;
  }

  private readStoredLocation(locationId: string | undefined): Location | undefined {
    return locationId ? this.locationsDataService.getById(locationId)() : undefined;
  }

  private resolveTask(report: WorkTimeReport, storedTask: Task | undefined): WorkTimeReportTask | undefined {
    if (report.task) {
      return { ...report.task };
    }

    if (!storedTask) {
      return undefined;
    }

    return {
      id: storedTask.id,
      name: storedTask.name,
      taskNumber: storedTask.taskNumber,
      taskType: storedTask.taskType?.(),
      estimation: storedTask.estimation
    };
  }

  private resolveProjectId(report: WorkTimeReport, storedTask: Task | undefined): string | undefined {
    if (report.projectId) {
      return report.projectId;
    }

    if (report.task || !storedTask) {
      return undefined;
    }

    return storedTask.project?.()?.id;
  }

  private resolveLocationId(report: WorkTimeReport, storedTask: Task | undefined): string | undefined {
    if (report.locationId) {
      return report.locationId;
    }

    if (report.task || !storedTask) {
      return undefined;
    }

    return storedTask.location?.()?.id;
  }

  private resolveProject(
    report: WorkTimeReport,
    storedProject: Project | undefined
  ): WorkTimeReportProject | undefined {
    if (report.project) {
      return { ...report.project };
    }

    if (!storedProject) {
      return undefined;
    }

    return { id: storedProject.id, name: storedProject.name };
  }

  private resolveLocation(
    report: WorkTimeReport,
    storedLocation: Location | undefined
  ): WorkTimeReportLocation | undefined {
    if (report.location) {
      return { ...report.location };
    }

    if (!storedLocation) {
      return undefined;
    }

    return { id: storedLocation.id, name: storedLocation.name };
  }
}
